import { useState } from 'react';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

/**
 * Custom hook for handling draggable button functionality
 * @param {boolean} isMobile - Whether the view is mobile
 * @returns {object} - Draggable state and handlers
 */
export const useDraggable = (isMobile) => {
    // Position calculation state - using bottom instead of top
    const [calculatedBottom, setCalculatedBottom] = useState(100); // 100px from bottom
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [dragStartBottom, setDragStartBottom] = useState(0);
    const [hasDragged, setHasDragged] = useState(false);

    const buttonHeight = isMobile ? 89 : 109;

    // Update position only on window resize to maintain bottom position
    // useEffectWithErrorLogging(() => {
    //     const updateButtonPosition = () => {
    //         // Position button 100px from bottom of viewport
    //         const buttonHeight = isMobile ? 89 : 109;
    //         const bottomOffset = 100;
    //         const newTop = window.innerHeight - buttonHeight - bottomOffset;
    //         setCalculatedTop(newTop);
    //     };

    //     // Initial calculation
    //     updateButtonPosition();

    //     // Listen for window resize only
    //     // window.addEventListener('resize', updateButtonPosition);

    //     // return () => {
    //     //     window.removeEventListener('resize', updateButtonPosition);
    //     // };
    // }, [isMobile]);

    /**
     * Handle drag start for mouse
     */
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setHasDragged(false);
        setDragStartY(e.clientY);
        setDragStartBottom(calculatedBottom);
        e.preventDefault();
    };

    /**
     * Handle drag start for touch
     */
    const handleTouchStart = (e) => {
        setIsDragging(true);
        setHasDragged(false);
        setDragStartY(e.touches[0].clientY);
        setDragStartBottom(calculatedBottom);
        e.preventDefault();
    };

    /**
     * Handle dragging
     */
    useEffectWithErrorLogging(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            const deltaY = e.clientY - dragStartY;
            // If moved more than 5 pixels, consider it a drag
            if (Math.abs(deltaY) > 5) {
                setHasDragged(true);
            }
            // Note: When dragging down (positive deltaY), bottom should decrease
            // When dragging up (negative deltaY), bottom should increase
            const newBottom = Math.max(
                20,
                Math.min(window.innerHeight - buttonHeight - 20, dragStartBottom - deltaY)
            );
            setCalculatedBottom(newBottom);
        };

        const handleTouchMove = (e) => {
            const deltaY = e.touches[0].clientY - dragStartY;
            // If moved more than 5 pixels, consider it a drag
            if (Math.abs(deltaY) > 5) {
                setHasDragged(true);
            }
            const newBottom = Math.max(
                20,
                Math.min(window.innerHeight - buttonHeight - 20, dragStartBottom - deltaY)
            );
            setCalculatedBottom(newBottom);
            e.preventDefault();
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        const handleTouchEnd = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, dragStartY, dragStartBottom, buttonHeight]);

    return {
        calculatedBottom,
        isDragging,
        hasDragged,
        handleMouseDown,
        handleTouchStart,
    };
};
